import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import ListasTab from "./ListasTab";

// Sub-components
import { TABS } from "./compras/constants";
import TareasPanel from "../components/TareasPanel";   // Fix CM (2026-09-13)
import { ocInvoiceComplete } from "../utils/invoices";
import { PedidosMaterialesTab } from "./compras/PedidosMaterialesTab";
import { AprobacionesTab } from "./compras/AprobacionesTab";
import { OrdenesTab } from "./compras/OrdenesTab";
import { FacturasTab } from "./compras/FacturasTab";
import { RetiroTab } from "./compras/RetiroTab";
import { ItinerarioWidget } from "./compras/ItinerarioWidget";
import { PedidosProveedoresTab } from "./compras/PedidosProveedoresTab";
import { AnalisisPresupuestoTab } from "./compras/AnalisisPresupuestoTab";
import { RangosAprobacionTab } from "./compras/RangosAprobacionTab";   // Fix EX
import { PtosFinalesTab } from "./compras/PtosFinalesTab";
import { Package } from "lucide-react";

export default function Purchases() {
  const { user } = useAuth();
  const isResidente = user?.role === "Residente de Obra";
  const isOperarioElectricista = user?.role === "Operario Electricista";
  const isOperarioOrResidente = isResidente || isOperarioElectricista;
  // BLOQUE 34 — deep-link desde el Dashboard por rol (?tab=<id>&open=<numero>):
  // abre directamente la pestaña + el detalle del documento clickeado.
  const [searchParams] = useSearchParams();
  const deepLinkTab = searchParams.get("tab");
  const openNumber = searchParams.get("open") || "";
  const [tab, setTab] = useState((deepLinkTab && (TABS.some(t => t.id === deepLinkTab) || deepLinkTab === "entrega_obra")) ? deepLinkTab : (isOperarioOrResidente ? "pedidos" : "listas"));
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [requests, setRequests] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [receptions, setReceptions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [pettyItems, setPettyItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [supplierOrders, setSupplierOrders] = useState([]);
  const [budgetActs, setBudgetActs] = useState([]);
  const [finalBudgets, setFinalBudgets] = useState([]);
  const [ocFromPF, setOcFromPF] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);

  // BLOQUE 27 PARTE A — catálogos + estado del filtro Cliente->Obra->Presupuesto
  // compartidos entre TODAS las pestañas (se cargan una sola vez acá arriba).
  const [listasClientes, setListasClientes] = useState([]);
  const [listasObras, setListasObras] = useState([]);
  const [allBudgets, setAllBudgets] = useState([]);
  const [filterCliente, setFilterCliente] = useState("");
  const [filterObra, setFilterObra] = useState("");
  const [filterPresupuesto, setFilterPresupuesto] = useState("");
  const filterProps = { filterCliente, setFilterCliente, filterObra, setFilterObra, filterPresupuesto, setFilterPresupuesto };

  const refreshBudgets = useCallback(() => {
    // Fix C (2026-08-27): allBudgets acá solo alimenta el filtro en cascada
    // Cliente → Obra → Presupuesto (número, nombre, estado). Los ítems se
    // piden aparte por /budgets/{id}/items-para-pedido, así que no hacen falta.
    axios.get(`${API}/budgets?light=true`, { withCredentials: true }).then(r => setAllBudgets(r.data || [])).catch(() => {});
  }, []);

  // BLOQUE 33 PARTE B — solo mostramos el Skeleton (loading=true) en la carga
  // INICIAL del módulo. En refrescos posteriores (ej. al cerrar un modal tras
  // editar/aprobar/generar algo) mantenemos la lista actual renderizada mientras
  // llega la data nueva — evita el remount a Skeleton que reseteaba el scroll
  // de vuelta al tope de cada pestaña.
  const hasLoadedOnceRef = useRef(false);
  const fetchAll = useCallback(async () => {
    if (!hasLoadedOnceRef.current) setLoading(true);
    try {
      const opts = { withCredentials: true };
      // Bloque 31 — el Operario Electricista solo puede consultar 4 endpoints
      // (requests, clientes/obras/materiales listas, budgets scrubeados). Todos
      // los demás endpoints de este módulo devolverían 403 y romperían el
      // Promise.all. Por eso hacemos un fetch reducido para este rol.
      if (isOperarioElectricista) {
        const [reqRes, clientesRes, obrasRes, budgetsRes, wdRes] = await Promise.all([
          axios.get(`${API}/purchases/requests`, opts),
          axios.get(`${API}/listas/clientes`, opts),
          axios.get(`${API}/listas/obras`, opts),
          axios.get(`${API}/budgets?light=true`, opts),  // Fix C (2026-08-27)
          // BLOQUE 44 — Entrega en Obra: listado ya acotado/scrubeado en el backend
          axios.get(`${API}/purchases/material-withdrawals`, opts),
        ]);
        setRequests(reqRes.data || []);
        setListasClientes(clientesRes.data || []);
        setListasObras(obrasRes.data || []);
        setAllBudgets(budgetsRes.data || []);
        setWithdrawals(wdRes.data || []);
        return;
      }
      const [provRes, reqRes, quotRes, ordRes, recRes, invRes, pettyRes, projRes, soRes, baRes, fbRes, ocRes, wdRes, clientesRes, obrasRes, budgetsRes] = await Promise.all([
        axios.get(`${API}/purchases/providers`, opts),
        axios.get(`${API}/purchases/requests`, opts),
        axios.get(`${API}/purchases/quotations`, opts),
        axios.get(`${API}/purchases/orders`, opts),
        axios.get(`${API}/purchases/receptions`, opts),
        axios.get(`${API}/purchases/invoices`, opts),
        axios.get(`${API}/purchases/petty-cash`, opts),
        axios.get(`${API}/projects`, opts),
        axios.get(`${API}/purchases/supplier-orders`, opts),
        axios.get(`${API}/purchases/budget-acts`, opts),
        axios.get(`${API}/purchases/final-budgets`, opts),
        axios.get(`${API}/purchases/oc-from-pf`, opts),
        axios.get(`${API}/purchases/material-withdrawals`, opts),
        axios.get(`${API}/listas/clientes`, opts),
        axios.get(`${API}/listas/obras`, opts),
        axios.get(`${API}/budgets?light=true`, opts),  // Fix C (2026-08-27)
      ]);
      setProviders(provRes.data);
      setRequests(reqRes.data);
      setQuotations(quotRes.data);
      setOrders(ordRes.data);
      setReceptions(recRes.data);
      setInvoices(invRes.data);
      setPettyItems(pettyRes.data);
      setProjects(projRes.data);
      setSupplierOrders(soRes.data);
      setBudgetActs(baRes.data);
      setFinalBudgets(fbRes.data);
      setOcFromPF(ocRes.data);
      setWithdrawals(wdRes.data);
      setListasClientes(clientesRes.data || []);
      setListasObras(obrasRes.data || []);
      setAllBudgets(budgetsRes.data || []);
    } catch {
      toast.error("Error cargando módulo de compras");
    } finally { setLoading(false); hasLoadedOnceRef.current = true; }
  }, [isOperarioElectricista]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const pendingApprovals = requests.filter(r => ["pendiente", "modificar"].includes(r.status)).length;
  const pendingPedidos = requests.filter(r => ["pendiente", "modificar"].includes(r.status)).length;
  const pendingPedProveedores = requests.filter(r =>
    r.status === "aprobada" && !supplierOrders.some(so => so.pm_id === r._id)
  ).length;
  const pendingAnalisis = budgetActs.filter(a => a.status === "pendiente" || a.status === "abierto").length;
  const pendingFinales = finalBudgets.filter(f => f.status === "pendiente").length;
  // BLOQUE 41 — una OC puede tener varias facturas: sigue "pendiente de factura"
  // mientras la cobertura esté incompleta (no cuando no tiene ninguna factura).
  const pendingFacturas = ocFromPF.filter(o => o.status !== "cancelada" && !ocInvoiceComplete(o)).length;
  const pendingRetiros = ocFromPF.filter(o => ["facturada", "facturada_parcial"].includes(o.status)).length;

  // Urgency breakdown (PDF COMPRAS REV pág 1/3/4/5/6/8: "Pend Crit PM-C/B/A")
  // Crítica (C) → Alta (B) → Normal (A). Mapeamos sobre la solicitud original (request).
  const urgBreakdown = (reqsArr) => {
    const out = { crit: 0, alta: 0, normal: 0 };
    reqsArr.forEach(r => {
      const u = (r?.urgency || "normal").toLowerCase();
      if (u === "critica" || u === "crítica" || u === "critico" || u === "crítico") out.crit++;
      else if (u === "alta") out.alta++;
      else out.normal++;
    });
    return out;
  };

  // "+ nuevos 1ros" (PDF COMPRAS REV): cantidad de items creados HOY (día calendario)
  // Indica el flujo entrante reciente en cada etapa del proceso de compras.
  const isToday = (dateStr) => {
    if (!dateStr) return false;
    try {
      const d = new Date(dateStr);
      const t = new Date();
      return d.getFullYear() === t.getFullYear() &&
             d.getMonth() === t.getMonth() &&
             d.getDate() === t.getDate();
    } catch { return false; }
  };
  const countNewToday = (arr) => (arr || []).filter(x => isToday(x?.created_at)).length;

  const pedidosPendList = requests.filter(r => ["pendiente", "modificar"].includes(r.status));
  const aprobPendList = pedidosPendList;
  const pedProvPendList = requests.filter(r =>
    r.status === "aprobada" && !supplierOrders.some(so => so.pm_id === r._id)
  );
  // For downstream tabs, derive urgency from linked request
  const reqById = Object.fromEntries(requests.map(r => [r._id || r.id, r]));
  const reqByPm = Object.fromEntries(requests.map(r => [r._id || r.id, r]));
  const reqByPp = {};
  supplierOrders.forEach(pp => { const r = reqByPm[pp.pm_id]; if (r) reqByPp[pp._id || pp.id] = r; });
  const reqByAp = {};
  budgetActs.forEach(a => { const r = reqByPp[a.pp_id]; if (r) reqByAp[a._id || a.id] = r; });
  const reqByPf = {};
  finalBudgets.forEach(f => { const r = reqByAp[f.ap_id]; if (r) reqByPf[f._id || f.id] = r; });
  const reqByOc = {};
  ocFromPF.forEach(o => {
    const pfIds = o.pf_ids || (o.pf_id ? [o.pf_id] : []);
    for (const pfId of pfIds) {
      const r = reqByPf[pfId];
      if (r) { reqByOc[o._id || o.id] = r; break; }
    }
  });

  const analisisPendList = budgetActs.filter(a => a.status === "pendiente" || a.status === "abierto").map(a => reqByPp[a.pp_id]).filter(Boolean);
  const ptosFinPendList = finalBudgets.filter(f => f.status === "pendiente").map(f => reqByAp[f.ap_id]).filter(Boolean);
  const facturasPendList = ocFromPF.filter(o => o.status !== "cancelada" && !o.invoice_number).map(o => reqByOc[o._id || o.id]).filter(Boolean);
  const retirosPendList = ocFromPF.filter(o => o.status === "facturada").map(o => reqByOc[o._id || o.id]).filter(Boolean);

  const breakdownByTab = {
    pedidos: urgBreakdown(pedidosPendList),
    aprobaciones: urgBreakdown(aprobPendList),
    ped_proveedores: urgBreakdown(pedProvPendList),
    analisis: urgBreakdown(analisisPendList),
    ptos_finales: urgBreakdown(ptosFinPendList),
    facturas: urgBreakdown(facturasPendList),
    retiro: urgBreakdown(retirosPendList),
  };

  // "+ nuevos 1ros" — items creados hoy en cada etapa
  // Para pedidos/aprobaciones: usa requests; downstream: usa created_at del documento de la etapa
  const newTodayByTab = {
    pedidos: countNewToday(pedidosPendList),
    aprobaciones: countNewToday(aprobPendList),
    ped_proveedores: countNewToday(supplierOrders),
    analisis: countNewToday(budgetActs.filter(a => a.status === "pendiente" || a.status === "abierto")),
    ptos_finales: countNewToday(finalBudgets.filter(f => f.status === "pendiente")),
    facturas: countNewToday(ocFromPF.filter(o => o.status !== "cancelada" && !ocInvoiceComplete(o))),
    retiro: countNewToday(ocFromPF.filter(o => ["facturada", "facturada_parcial"].includes(o.status))),
  };

  const pendingBadge = (id) => {
    if (id === "pedidos") return pendingPedidos;
    if (id === "aprobaciones") return pendingApprovals;
    if (id === "ped_proveedores") return pendingPedProveedores;
    if (id === "analisis") return pendingAnalisis;
    if (id === "ptos_finales") return pendingFinales;
    if (id === "facturas") return pendingFacturas;
    if (id === "retiro") return pendingRetiros;
    // BLOQUE 44 — entregas en obra pendientes de confirmar por el Operario Electricista
    if (id === "entrega_obra") return withdrawals.filter(w => w.status === "retiro_cerrado").length;
    return 0;
  };

  // BLOQUE 44 — pestañas exclusivas del Operario Electricista: sus pedidos,
  // las aprobaciones de esos pedidos, y SOLO la confirmación de Entrega en
  // Obra (nunca "Retiro de Materiales", que sigue siendo de Compras/Logística).
  const operarioTabs = [
    ...TABS.filter(t => t.id === "pedidos" || t.id === "aprobaciones"),
    { id: "entrega_obra", label: "Entrega en Obra", icon: Package },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="purchases-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Compras de Materiales</h1>
        <p className="text-sm text-gray-500 mt-0.5">Flujo completo: pedido → aprobación → PP → análisis → OC → factura → entrega</p>
      </div>

      {/* BLOQUE 55 — Residente de Obra y Operario Electricista no tienen
          Dashboard propio (aterrizan directo acá por su sandbox) — reciben
          el bloque de itinerario arriba de sus pestañas. Compacto/colapsable
          para el Operario, cuya pantalla principal es crear pedidos. */}
      {isOperarioOrResidente && (
        <ItinerarioWidget linkTab={isOperarioElectricista ? "entrega_obra" : "retiro"} collapsible={isOperarioElectricista} />
      )}

      {/* Tabs */}
      {/* Fix EU (2026-10-01): en el celular las pestañas se deslizan de costado y muestran su nombre */}
      <div className="flex flex-wrap gap-0.5 pb-0 pt-1.5 lg:pt-0 border-b border-gray-200" data-testid="purchases-tabs">
        {(isOperarioElectricista
          ? operarioTabs
          : isResidente
          ? TABS.filter(t => t.id === "pedidos" || t.id === "retiro")
          : TABS
        ).map(({ id, label, icon: Icon }) => {
          const badge = pendingBadge(id);
          const bd = breakdownByTab[id];
          const newToday = newTodayByTab[id] || 0;
          const tooltipParts = [];
          if (bd) tooltipParts.push(`Pendientes — Crítica: ${bd.crit} · Alta: ${bd.alta} · Normal: ${bd.normal}`);
          if (newToday > 0) tooltipParts.push(`+ ${newToday} nuevos hoy`);
          const tooltip = tooltipParts.length ? tooltipParts.join(" · ") : undefined;
          return (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`tab-${id}`}
              title={tooltip}
              className={`flex items-center gap-1 px-2 py-2.5 text-xs font-semibold rounded-t-lg transition-all whitespace-nowrap relative ${
                tab === id
                  ? "text-[#2E6DA4] border-b-2 border-[#2E6DA4] bg-blue-50/60"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50/60"
              }`}
            >
              <Icon size={13} />
              <span>{label}</span>
              {badge > 0 && (
                <span data-testid={`badge-${id}`} className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-amber-400 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {badge}
                </span>
              )}
              {bd && (bd.crit > 0 || bd.alta > 0 || bd.normal > 0) && (
                <span data-testid={`urg-breakdown-${id}`} className="hidden md:flex items-center gap-px ml-0.5 text-[9px] font-bold">
                  {bd.crit > 0 && (
                    <span className="bg-red-100 text-red-700 rounded px-0.5 leading-tight" title="Crítica">{bd.crit}C</span>
                  )}
                  {bd.alta > 0 && (
                    <span className="bg-amber-100 text-amber-700 rounded px-0.5 leading-tight" title="Alta">{bd.alta}B</span>
                  )}
                  {bd.normal > 0 && (
                    <span className="bg-blue-100 text-blue-700 rounded px-0.5 leading-tight" title="Normal">{bd.normal}A</span>
                  )}
                </span>
              )}
              {newToday > 0 && (
                <span
                  data-testid={`new-today-${id}`}
                  className="hidden md:flex items-center ml-px text-[9px] font-bold bg-emerald-100 text-emerald-700 rounded px-0.5 leading-tight"
                  title={`+ ${newToday} nuevos hoy`}
                >
                  +{newToday}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="min-h-64">
        {tab === "listas" && <ListasTab />}
        {tab === "pedidos" && <PedidosMaterialesTab requests={requests} projects={projects} loading={loading} onRefresh={fetchAll} userRole={user?.role} listasClientes={listasClientes} listasObras={listasObras} allBudgets={allBudgets} refreshBudgets={refreshBudgets} openNumber={openNumber} {...filterProps} />}
        {tab === "aprobaciones" && <AprobacionesTab requests={requests} loading={loading} onRefresh={fetchAll} userRole={user?.role} listasClientes={listasClientes} listasObras={listasObras} allBudgets={allBudgets} {...filterProps} />}
        {tab === "ped_proveedores" && <PedidosProveedoresTab supplierOrders={supplierOrders} requests={requests} loading={loading} onRefresh={fetchAll} userRole={user?.role} listasClientes={listasClientes} listasObras={listasObras} allBudgets={allBudgets} {...filterProps} />}
        {tab === "analisis" && <AnalisisPresupuestoTab budgetActs={budgetActs} supplierOrders={supplierOrders} loading={loading} onRefresh={fetchAll} userRole={user?.role} listasClientes={listasClientes} listasObras={listasObras} allBudgets={allBudgets} {...filterProps} />}
        {tab === "ptos_finales" && <PtosFinalesTab finalBudgets={finalBudgets} budgetActs={budgetActs} loading={loading} onRefresh={fetchAll} userRole={user?.role} listasClientes={listasClientes} listasObras={listasObras} allBudgets={allBudgets} {...filterProps} />}
        {tab === "ordenes" && <OrdenesTab ocFromPF={ocFromPF} finalBudgets={finalBudgets} loading={loading} onRefresh={fetchAll} userRole={user?.role} listasClientes={listasClientes} listasObras={listasObras} allBudgets={allBudgets} {...filterProps} />}
        {tab === "facturas" && <FacturasTab ocFromPF={ocFromPF} loading={loading} onRefresh={fetchAll} userRole={user?.role} listasClientes={listasClientes} listasObras={listasObras} allBudgets={allBudgets} {...filterProps} />}
        {tab === "retiro" && <RetiroTab withdrawals={withdrawals} ocFromPF={ocFromPF} loading={loading} onRefresh={fetchAll} userRole={user?.role} listasClientes={listasClientes} listasObras={listasObras} allBudgets={allBudgets} openNumber={openNumber} {...filterProps} />}
        {tab === "entrega_obra" && <RetiroTab withdrawals={withdrawals} ocFromPF={ocFromPF} loading={loading} onRefresh={fetchAll} userRole={user?.role} listasClientes={listasClientes} listasObras={listasObras} allBudgets={allBudgets} openNumber={openNumber} operarioMode {...filterProps} />}
        {/* Fix CM (2026-09-13): tareas del flujo de trabajo de Compras. */}
        {tab === "flujo" && <TareasPanel modulo="compras" titulo="Tareas y Flujo de Trabajo — Compras" />}
        {/* Fix EX (2026-10-05): quién aprueba pedidos y análisis, por perfil y por monto. */}
        {tab === "rangos" && <RangosAprobacionTab />}
      </div>
    </div>
  );
}
